import { Component, Host, h, Element, Prop, Event, EventEmitter, Method, Watch } from "@stencil/core";

import {
  StzhDisturberOpenEvent,
  StzhDisturberOpenedEvent,
  StzhDisturberCloseEvent,
  StzhDisturberClosedEvent,
} from "../../../index";

import { StzhDisturberLocalizedText } from "./stzh-disturber.localization";
import { hasSlot, setPropsIfNull } from "../../../utils/utils";
import { waitForEvent } from "../../../utils/event-utils";
import { animateTo, stopAnimations } from "../../../utils/animation-utils";
import { media } from "../../../utils/media-utils";
import Cookie from "js-cookie";

const KEY_PREFIX_CLOSE = "stzh-components-disturber-close";

function getAnimationShow() {
  const isMedium = media("medium").matches;

  return {
    keyframes: [
      { transform: isMedium ? "translateY(40px)" : "translateY(100%)", opacity: "0" },
      { transform: "translateY(0px)", opacity: "1" },
    ],
    options: {
      duration: 300,
      easing: "ease-out",
    },
  };
}

function getAnimationHide() {
  const isMedium = media("medium").matches;

  return {
    keyframes: [
      { transform: "translateY(0px)", opacity: "1" },
      { transform: isMedium ? "translateY(40px)" : "translateY(100%)", opacity: "0" },
    ],
    options: {
      duration: 300,
      easing: "ease-in",
    },
  };
}

/**
 * @slot label - Slot for label (use as alternative for property)
 * @slot link - Slot for optional stzh-link element
 */
@Component({
  tag: "stzh-disturber",
  styleUrl: "stzh-disturber.scss",
  shadow: true,
})
export class StzhDisturber {
  /** Translation strings. */
  @Prop() localization: StzhDisturberLocalizedText;

  /** Label (slot can also be used as alternative) */
  @Prop() label: string = "";

  /** Whether to hide close button */
  @Prop({ reflect: true }) hideClose: boolean = false;

  /** Open state */
  @Prop({ reflect: true }) open: boolean = true;

  /** Whether open animation should be played initially. */
  @Prop() initialOpenAnimation = false;

  /**
   * Close cookie name key.
   * Component will check for the cookie (stzh-components-disturber-close-{name})
   * and if not empty (disturber already closed before) will hide the disturber.
   */
  @Prop() closeKey: string = "";

  /** Disturber open event */
  @Event() stzhOpen: EventEmitter<StzhDisturberOpenEvent>;

  /** Disturber opened event (after animation)  */
  @Event() stzhOpened: EventEmitter<StzhDisturberOpenedEvent>;

  /** Disturber close event */
  @Event() stzhClose: EventEmitter<StzhDisturberCloseEvent>;

  /** Disturber closed event (after animation)  */
  @Event() stzhClosed: EventEmitter<StzhDisturberClosedEvent>;

  @Element() element: HTMLStzhDisturberElement;

  // @State() loaded: boolean = false;

  // @Listen("appload", { target: "window" })
  // handleAppload() {
  //   if (this.initialOpenAnimation && this.open && !this.loaded) {
  //     this.loaded = true;

  //     console.log('appload');

  //     requestAnimationFrame(() => {
  //       requestAnimationFrame(() => {
  //         window.setTimeout(() => {
  //           this.openWatcher(true);
  //         }, 4000);
  //       });
  //     });
  //   }
  // }

  @Watch("open")
  async openWatcher(newValue: boolean) {
    if (!this.rootElement) {
      return;
    }

    if (newValue) {
      if (this.toggledByMethod) {
        this.stzhOpen.emit({
          component: "stzh-disturber",
        });
      }

      await stopAnimations(this.element);

      // this.element.style.willChange = "transform, opacity, height";
      this.element.hidden = false;

      const animationShow = getAnimationShow();

      await animateTo(this.element, animationShow.keyframes, animationShow.options);

      this.element.style.opacity = "1";
      // this.element.style.willChange = "auto";

      if (this.toggledByMethod) {
        this.stzhOpened.emit({
          component: "stzh-disturber",
        });
      }
    } else {
      if (this.toggledByMethod) {
        this.stzhClose.emit({
          component: "stzh-disturber",
        });
      }

      await stopAnimations(this.element);

      // this.element.style.willChange = "transform, opacity, height";

      const animationHide = getAnimationHide();

      await animateTo(this.element, animationHide.keyframes, animationHide.options);

      this.element.style.opacity = "0";
      this.element.hidden = true;
      // this.element.style.willChange = "auto";

      if (this.toggledByMethod) {
        this.stzhClosed.emit({
          component: "stzh-disturber",
        });
      }
    }

    this.toggledByMethod = false;
  }

  @Method()
  async show() {
    if (this.open) {
      return;
    }

    this.toggledByMethod = true;
    this.open = true;
    return waitForEvent(this.element, "stzhOpened");
  }

  @Method()
  async hide(setCloseKey: boolean = true) {
    if (!this.open) {
      return;
    }

    if (this.closeKey && setCloseKey) {
      Cookie.set(`${KEY_PREFIX_CLOSE}-${this.closeKey}`, Date.now().toString());
    }

    this.toggledByMethod = true;
    this.open = false;
    return waitForEvent(this.element, "stzhClosed");
  }

  private toggledByMethod: boolean;
  private rootElement: HTMLElement;

  private handleLinkSlotchange = () => {
    const host = (this.element.getRootNode() as ShadowRoot)?.host || this.element;
    const links = host.querySelectorAll('stzh-link[slot="link"], [slot="link"] stzh-link');

    links.forEach(link => {
      setPropsIfNull(link, {
        icon: "arrow-right",
        iconPosition: "right",
      } as HTMLStzhLinkElement);
    });
  };

  private onCloseButtonClick = () => {
    this.hide();
  };

  async componentWillLoad() {
    if (this.closeKey) {
      const timeClosed = Cookie.get(`${KEY_PREFIX_CLOSE}-${this.closeKey}`);
      if (timeClosed) {
        this.hide(false);
      }
    }

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "disturber");
    }
  }

  componentDidLoad() {
    if (this.initialOpenAnimation && this.open) {
      // hide first so the open animation will be shown
      this.element.hidden = true;
      this.element.style.opacity = "0";

      // set flag because we want stzhOpen event to be triggered
      this.toggledByMethod = true;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.setTimeout(() => {
            this.openWatcher(true);
          }, 250);
        });
      });
    } else {
      this.element.hidden = !this.open;
      this.element.style.opacity = this.open ? "1" : "0";
    }
  }

  render() {
    const linkUsed = hasSlot(this.element, "link");

    const classes = {
      "stzh-disturber": true,
      "stzh-disturber--has-link": linkUsed,
      "stzh-disturber--has-close-hidden": this.hideClose,
    };

    return (
      <Host>
        <div ref={el => (this.rootElement = el as HTMLElement)} class={classes}>
          <div class="stzh-disturber__content">
            <div class="stzh-disturber__label">{this.label ? this.label : <slot name="label"></slot>}</div>
            <div class="stzh-disturber__link">
              <slot name="link" onSlotchange={this.handleLinkSlotchange}></slot>
            </div>
          </div>

          {!this.hideClose && (
            <div class="stzh-disturber__close-wrapper">
              <stzh-button
                class="stzh-disturber__close"
                onClick={this.onCloseButtonClick}
                label={this.localization.close}
                variant="tertiary"
                size="small"
                iconOnly={true}
                icon="close-big"
              ></stzh-button>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
